import { Controller } from "@hotwired/stimulus"

// サイネージ操作用のStimulusコントローラー
// アコーディオンの開閉、選択状態の管理、表示上の処理を実装
export default class extends Controller {
  static targets = [
    "accordionItem",
    "accordionTitle",
    "accordionContent",
    "selectedValue",
    "menuSelectButton",
    "displayTypeButton",
    "menuDisplayList",
    "orderDisplayLeft",
    "orderDisplayRight",
    "orderItemAddButton",
    "orderItemRemoveButton",
    "orderClearAllButton",
    "menuDisplayDecisionButton",
    "orderDisplayDecisionButton",
    "applyButton",
    "resetButton",
  ]

  declare readonly accordionItemTargets: HTMLElement[]
  declare readonly accordionTitleTargets: HTMLElement[]
  declare readonly accordionContentTargets: HTMLElement[]
  declare readonly selectedValueTargets: HTMLElement[]
  declare readonly menuSelectButtonTargets: HTMLButtonElement[]
  declare readonly displayTypeButtonTargets: HTMLButtonElement[]
  declare readonly menuDisplayListTarget: HTMLElement
  declare readonly orderDisplayLeftTarget: HTMLElement
  declare readonly orderDisplayRightTarget: HTMLElement
  declare readonly orderItemAddButtonTargets: HTMLButtonElement[]
  declare readonly orderItemRemoveButtonTargets: HTMLButtonElement[]
  declare readonly orderClearAllButtonTarget: HTMLButtonElement
  declare readonly menuDisplayDecisionButtonTarget: HTMLButtonElement
  declare readonly orderDisplayDecisionButtonTarget: HTMLButtonElement
  declare readonly applyButtonTarget: HTMLButtonElement
  declare readonly resetButtonTarget: HTMLButtonElement

  // 選択状態を保持
  private selectedMenuId: string | null = null
  private selectedDisplayType: "menu" | "order" | null = null
  private selectedOrderItems: Array<{ id: string; name: string; color: string; masterName: string; labelColor: string }> = []
  private isReflected: boolean = false
  private readonly MAX_ORDER_ITEMS = 4

  // 色の一覧（日本語ラベル、英語値）
  private readonly COLOR_OPTIONS = [
    { label: "青", value: "blue" },
    { label: "ピンク", value: "pink" },
    { label: "黄", value: "yellow" },
    { label: "紫", value: "purple" },
    { label: "オレンジ", value: "orange" },
    { label: "赤", value: "red" },
    { label: "緑", value: "green" },
  ]

  // セクションの状態
  private section1Completed: boolean = false
  private section2Completed: boolean = false
  private section3Completed: boolean = false

  connect() {
    // ページ読み込み時：セクション1が開いた状態
    this.openSection(0)
  }

  // セクション1: メニュー選択
  selectMenu(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLButtonElement
    const menuId = button.dataset.menuId || null

    if (!menuId) return

    // 選択状態を更新
    this.selectedMenuId = menuId

    // ボタンの視覚的更新
    this.menuSelectButtonTargets.forEach((btn) => {
      if (btn.dataset.menuId === menuId) {
        btn.classList.remove("uk-button-default")
        btn.classList.add("uk-button-secondary")
      } else {
        btn.classList.remove("uk-button-secondary")
        btn.classList.add("uk-button-default")
      }
    })

    // 選択値を表示
    const menuName = button.dataset.menuName || ""
    const tableNumber = button.dataset.tableNumber || ""
    this.updateSelectedValue(0, `${menuName} #${tableNumber}`)

    // 選択されたメニューのmenu_itemsを表示
    this.showSelectedMenuItems(menuId)

    // 変更が入ったので反映ボタンを有効化
    this.markDirty()

    // セクション1を折りたたみ、セクション2を開く
    this.collapseSection(0)
    this.section1Completed = true
    this.openSection(1)
    this.updateSection2State()
  }

  // 選択されたメニューのmenu_itemsを表示
  private showSelectedMenuItems(menuId: string) {
    // メニュー表示のコンテナを表示
    const menuDisplayContainers = this.element.querySelectorAll(`[data-menu-id="${menuId}"].signage-menu-items-container`)
    menuDisplayContainers.forEach((container) => {
      const htmlContainer = container as HTMLElement
      htmlContainer.style.display = "block"
    })

    // 他のメニューのコンテナを非表示
    const allMenuDisplayContainers = this.element.querySelectorAll(".signage-menu-items-container")
    allMenuDisplayContainers.forEach((container) => {
      const htmlContainer = container as HTMLElement
      if (htmlContainer.dataset.menuId !== menuId) {
        htmlContainer.style.display = "none"
      }
    })

    // 注文表示の利用可能なメニュー項目を表示
    const orderAvailableContainers = this.element.querySelectorAll(`[data-menu-id="${menuId}"].signage-order-available-items`)
    orderAvailableContainers.forEach((container) => {
      const htmlContainer = container as HTMLElement
      htmlContainer.style.display = "block"
    })

    // 他のメニューの利用可能なメニュー項目を非表示
    const allOrderAvailableContainers = this.element.querySelectorAll(".signage-order-available-items")
    allOrderAvailableContainers.forEach((container) => {
      const htmlContainer = container as HTMLElement
      if (htmlContainer.dataset.menuId !== menuId) {
        htmlContainer.style.display = "none"
      }
    })
  }

  // セクション2: 表示内容選択
  selectDisplayType(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLButtonElement
    const displayType = button.dataset.displayType as "menu" | "order"

    if (!displayType) return

    // 選択状態を更新
    this.selectedDisplayType = displayType

    // ボタンの視覚的更新
    this.displayTypeButtonTargets.forEach((btn) => {
      if (btn.dataset.displayType === displayType) {
        btn.classList.remove("uk-button-default")
        btn.classList.add("uk-button-secondary")
      } else {
        btn.classList.remove("uk-button-secondary")
        btn.classList.add("uk-button-default")
      }
    })

    // 表示内容を切り替え
    if (displayType === "menu") {
      this.showMenuDisplay()
    } else {
      this.showOrderDisplay()
    }

    // 変更が入ったので反映ボタンを有効化
    this.markDirty()
  }

  // メニュー表示を表示
  private showMenuDisplay() {
    this.setDisplay(this.element.querySelector(".signage-menu-display"), true)
    this.setDisplay(this.element.querySelector(".signage-order-display"), false)
  }

  // 注文表示を表示
  private showOrderDisplay() {
    this.setDisplay(this.element.querySelector(".signage-menu-display"), false)
    this.setDisplay(this.element.querySelector(".signage-order-display"), true)

    // 選択されたメニューのmenu_itemsを表示
    if (this.selectedMenuId) {
      this.showSelectedMenuItems(this.selectedMenuId)
    }

    // 初期状態でも0件表示が出るように更新
    this.updateOrderDisplay()
  }

  // メニュー表示の決定
  decideMenuDisplay(event: Event) {
    event.preventDefault()
    this.updateSelectedValue(1, "メニュー表示")
    this.collapseSection(1)
    this.section2Completed = true
    this.openSection(2)
    this.updateSection3State()
  }

  // 注文表示: 項目を追加
  addOrderItem(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLButtonElement
    const itemId = button.dataset.itemId || ""
    const itemName = button.dataset.itemName || ""
    const masterName = button.dataset.itemMasterName || ""
    const labelColor = button.dataset.itemLabelColor || ""

    if (!itemId || !itemName) return

    // 最大4件まで
    if (this.selectedOrderItems.length >= 4) return

    // 選択済み項目に追加（重複可、初期色は最初の色）
    const defaultColor = this.COLOR_OPTIONS[0].value
    this.selectedOrderItems.push({ id: itemId, name: itemName, color: defaultColor, masterName, labelColor })
    this.updateOrderDisplay()
    this.updateOrderDecisionButton()
    this.markDirty()
  }

  // 注文表示: 項目を削除
  removeOrderItem(event: Event) {
    event.preventDefault()
    const button = event.currentTarget as HTMLButtonElement
    const index = parseInt(button.dataset.index || "0", 10)

    if (isNaN(index)) return

    // 選択済み項目から削除
    this.selectedOrderItems.splice(index, 1)
    this.updateOrderDisplay()
    this.updateOrderDecisionButton()
    this.markDirty()
  }

  // 注文表示: 全解除
  clearAllOrderItems(event: Event) {
    event.preventDefault()
    this.selectedOrderItems = []
    this.updateOrderDisplay()
    this.updateOrderDecisionButton()
    this.markDirty()
  }

  // 注文表示: 項目の色を変更
  changeOrderItemColor(event: Event) {
    event.preventDefault()
    const select = event.currentTarget as HTMLSelectElement
    const index = parseInt(select.dataset.index || "0", 10)

    if (isNaN(index) || index < 0 || index >= this.selectedOrderItems.length) return

    const newColor = select.value
    this.selectedOrderItems[index].color = newColor
    this.markDirty()
  }

  // 注文表示の表示を更新
  private updateOrderDisplay() {
    const container = this.orderDisplayRightTarget.querySelector("[data-signage-target='orderItemsContainer']")
    if (!container) return

    // 既存の項目をクリア
    container.innerHTML = ""

    if (this.selectedOrderItems.length === 0) {
      const placeholder = document.createElement("div")
      placeholder.className = "uk-text-muted uk-text-small uk-text-center uk-padding-small"
      placeholder.textContent = "選択済み 0件"
      container.appendChild(placeholder)
      return
    }

    // 選択済み項目を表示
    this.selectedOrderItems.forEach((item, index) => {
      const itemElement = document.createElement("div")
      const isLast = index === this.selectedOrderItems.length - 1
      itemElement.className = `signage-order-item uk-flex uk-flex-between uk-flex-middle${isLast ? "" : " uk-margin-small-bottom"}`

      // メニュー名（左側）- ラベル付き
      const nameContainer = document.createElement("span")

      // 商品名ラベル
      const labelSpan = document.createElement("span")
      labelSpan.className = "uk-label"
      labelSpan.style.backgroundColor = item.labelColor
      labelSpan.textContent = item.masterName

      // カスタム名
      const nameSpan = document.createElement("span")
      nameSpan.className = "uk-margin-small-left"
      nameSpan.textContent = item.name

      nameContainer.appendChild(labelSpan)
      nameContainer.appendChild(nameSpan)

      // 右側のコンテナ（セレクトタグと削除ボタン）
      const rightContainer = document.createElement("div")
      rightContainer.className = "uk-flex uk-flex-middle"
      rightContainer.style.gap = "0.5rem"

      // 色選択セレクトタグ
      const selectElement = document.createElement("select")
      selectElement.className = "uk-select uk-form-small"
      selectElement.style.width = "auto"
      selectElement.dataset.index = index.toString()
      selectElement.dataset.action = "change->signage#changeOrderItemColor"

      // オプションを追加
      this.COLOR_OPTIONS.forEach((colorOption) => {
        const option = document.createElement("option")
        option.value = colorOption.value
        option.textContent = colorOption.label
        if (item.color === colorOption.value) {
          option.selected = true
        }
        selectElement.appendChild(option)
      })

      rightContainer.appendChild(selectElement)

      // 削除ボタン
      const removeButton = document.createElement("button")
      removeButton.className = "uk-button uk-button-small uk-button-default"
      removeButton.textContent = "×"
      removeButton.dataset.signageTarget = "orderItemRemoveButton"
      removeButton.dataset.action = "click->signage#removeOrderItem"
      removeButton.dataset.index = index.toString()

      rightContainer.appendChild(removeButton)

      itemElement.appendChild(nameContainer)
      itemElement.appendChild(rightContainer)
      container.appendChild(itemElement)
    })
  }

  // 注文表示の決定ボタンの状態を更新
  private updateOrderDecisionButton() {
    if (this.selectedOrderItems.length === 0) {
      this.orderDisplayDecisionButtonTarget.disabled = true
    } else {
      this.orderDisplayDecisionButtonTarget.disabled = false
    }
  }

  // 注文表示の決定
  decideOrderDisplay(event: Event) {
    event.preventDefault()
    const count = this.selectedOrderItems.length
    this.updateSelectedValue(1, `注文表示（${count}件）`)
    this.collapseSection(1)
    this.section2Completed = true
    this.openSection(2)
    this.updateSection3State()

    // 変更が入ったので反映ボタンを有効化
    this.markDirty()
  }

  // セクション3: 反映処理
  async apply(event: Event) {
    event.preventDefault()

    // 反映ボタンを無効化（二重送信防止）
    this.applyButtonTarget.disabled = true

    try {
      const payload = this.buildApplyPayload()
      const response = await fetch('/admin/signage/apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
        },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        this.isReflected = true
        this.updateSelectedValue(2, "反映済み")
        this.collapseSection(2)
        this.section3Completed = true
        this.showToast("反映しました", "success")
      } else {
        const data = await response.json()
        this.showToast(data.errors?.join(', ') || "反映に失敗しました", "danger")
        this.applyButtonTarget.disabled = false
      }
    } catch (error) {
      console.error('[Signage] Apply error:', error)
      this.showToast("反映に失敗しました", "danger")
      this.applyButtonTarget.disabled = false
    }
  }

  // Build payload for apply API
  private buildApplyPayload(): object {
    const payload: Record<string, unknown> = {
      menu_id: this.selectedMenuId,
    }

    if (this.selectedDisplayType === 'menu') {
      payload.mode = 'menu'
      payload.order_items = []
    } else if (this.selectedDisplayType === 'order') {
      payload.mode = 'order'
      payload.order_items = this.selectedOrderItems.map(item => ({
        menu_item_id: item.id,
        name: item.name,
        color: item.color,
      }))
    }

    return payload
  }

  // リセット機能
  async reset(event: Event) {
    event.preventDefault()

    // リセットボタンを無効化（二重送信防止）
    this.resetButtonTarget.disabled = true

    try {
      const response = await fetch('/admin/signage/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken,
        },
      })

      if (response.ok) {
        this.resetLocalState()
        this.showToast("待機表示に戻しました", "success")
      } else {
        this.showToast("リセットに失敗しました", "danger")
      }
    } catch (error) {
      console.error('[Signage] Reset error:', error)
      this.showToast("リセットに失敗しました", "danger")
    } finally {
      this.resetButtonTarget.disabled = false
    }
  }

  // Reset local UI state
  private resetLocalState() {
    // 選択状態をリセット
    this.selectedMenuId = null
    this.selectedDisplayType = null
    this.selectedOrderItems = []
    this.isReflected = false
    this.section1Completed = false
    this.section2Completed = false
    this.section3Completed = false

    // ボタンの状態をリセット
    this.menuSelectButtonTargets.forEach((btn) => {
      btn.classList.remove("uk-button-secondary")
      btn.classList.add("uk-button-default")
    })

    this.displayTypeButtonTargets.forEach((btn) => {
      btn.classList.remove("uk-button-secondary")
      btn.classList.add("uk-button-default")
    })

    // 選択値をクリア
    this.selectedValueTargets.forEach((target) => {
      target.textContent = ""
    })

    // メニュー表示・注文表示を非表示
    const menuDisplayContainer = this.element.querySelector(".signage-menu-display")
    if (menuDisplayContainer) {
      const htmlContainer = menuDisplayContainer as HTMLElement
      htmlContainer.style.display = "none"
    }

    const orderDisplayContainer = this.element.querySelector(".signage-order-display")
    if (orderDisplayContainer) {
      const htmlContainer = orderDisplayContainer as HTMLElement
      htmlContainer.style.display = "none"
    }

    // すべてのメニューのコンテナを非表示
    const allMenuDisplayContainers = this.element.querySelectorAll(".signage-menu-items-container")
    allMenuDisplayContainers.forEach((container) => {
      const htmlContainer = container as HTMLElement
      htmlContainer.style.display = "none"
    })

    const allOrderAvailableContainers = this.element.querySelectorAll(".signage-order-available-items")
    allOrderAvailableContainers.forEach((container) => {
      const htmlContainer = container as HTMLElement
      htmlContainer.style.display = "none"
    })

    // 注文表示の選択済み項目をクリア
    this.updateOrderDisplay()

    // 反映ボタンを有効化
    this.applyButtonTarget.disabled = false
    this.isReflected = false

    // すべてのセクションを閉じる
    this.accordionItemTargets.forEach((item, index) => {
      this.collapseSection(index)
    })

    // セクション1のみ開く
    this.openSection(0)
  }

  // アコーディオンの開閉管理
  private openSection(index: number) {
    if (index >= this.accordionItemTargets.length) return

    const item = this.accordionItemTargets[index]
    if (!item) return

    // UIKitのアコーディオンを開く
    const uikit = (window as any).UIkit
    if (typeof uikit !== "undefined") {
      const accordion = item.closest("ul")
      if (accordion) {
        const accordionInstance = uikit.accordion(accordion)
        if (accordionInstance) {
          // アコーディオンが閉じている場合は開く
          const isOpen = item.classList.contains("uk-open")
          if (!isOpen) {
            accordionInstance.toggle(index, false)
          }
        }
      }
    }
  }

  private collapseSection(index: number) {
    if (index >= this.accordionItemTargets.length) return

    const item = this.accordionItemTargets[index]
    if (!item) return

    // UIKitのアコーディオンを閉じる
    const uikit = (window as any).UIkit
    if (typeof uikit !== "undefined") {
      const accordion = item.closest("ul")
      if (accordion) {
        const accordionInstance = uikit.accordion(accordion)
        if (accordionInstance) {
          // アコーディオンが開いている場合は閉じる
          const isOpen = item.classList.contains("uk-open")
          if (isOpen) {
            accordionInstance.toggle(index, false)
          }
        }
      }
    }
  }

  // 選択値を更新
  private updateSelectedValue(sectionIndex: number, value: string) {
    if (sectionIndex >= this.selectedValueTargets.length) return

    const target = this.selectedValueTargets[sectionIndex]
    if (target) {
      target.textContent = value
      if (value && value.length > 0) {
        target.classList.add("has-value")
      } else {
        target.classList.remove("has-value")
      }
    }
  }

  // セクション2の状態を更新（ステップの順序性）
  private updateSection2State() {
    if (!this.section1Completed) {
      // セクション1が未選択の場合、セクション2を透明度を下げる
      if (this.accordionItemTargets[1]) {
        this.accordionItemTargets[1].style.opacity = "0.5"
      }
    } else {
      // セクション1が選択済みの場合、通常の透明度
      if (this.accordionItemTargets[1]) {
        this.accordionItemTargets[1].style.opacity = "1"
      }
    }
  }

  // セクション3の状態を更新
  private updateSection3State() {
    if (!this.section2Completed) {
      // セクション2が未選択の場合、セクション3を透明度を下げる
      if (this.accordionItemTargets[2]) {
        this.accordionItemTargets[2].style.opacity = "0.5"
      }
    } else {
      // セクション2が選択済みの場合、通常の透明度
      if (this.accordionItemTargets[2]) {
        this.accordionItemTargets[2].style.opacity = "1"
      }
    }
  }

  // トーストを表示
  private showToast(message: string, status: "success" | "danger" = "success") {
    const uikit = this.uiKit
    if (typeof uikit !== "undefined") {
      uikit.notification({
        message: message,
        status: status,
        pos: "top-right",
        timeout: 3000,
      })
    }
  }

  // 共通: display切り替え
  private setDisplay(element: Element | null, show: boolean) {
    if (!element) return
    const html = element as HTMLElement
    html.style.display = show ? "block" : "none"
  }

  // UIKit accessor
  private get uiKit() {
    return (window as any).UIkit
  }

  // CSRF token accessor
  private get csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta?.getAttribute('content') || ''
  }

  // 変更が入ったら反映ボタンを再度有効化する
  private markDirty() {
    this.isReflected = false
    this.applyButtonTarget.disabled = false
  }
}

